import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { RootState } from "../../../store";
import { fetchParentCategories } from "../../../Redux/filters/slice";
import type { Category } from "../../../Redux/filters/types";

interface Props {
  selectedCategory: string; // super category name, or "ALL"
}

const FALLBACK_IMAGE = "../../assets/img/categories/allcategories.png";

const MiniSubCategories: React.FC<Props> = ({ selectedCategory }) => {
  const dispatch = useAppDispatch();
  const { data, loading, error } = useAppSelector(
    (state: RootState) => state.filters.parentCategories,
  );

  useEffect(() => {
    dispatch(
      fetchParentCategories({
        superCategory: selectedCategory !== "ALL" ? selectedCategory : undefined,
        showOnWebsite: true,
        showOnFilters: true,
      }),
    );
  }, [dispatch, selectedCategory]);

  if (loading && data.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center w-100 py-5">
        <div className="spinner-border spinner-border-sm text-dark" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (error && data.length === 0) {
    return <div className="text-center text-danger text-sm w-100 py-5">{error}</div>;
  }

  if (data.length === 0) {
    return (
      <div className="text-center text-muted text-sm w-100 py-5">
        No categories found.
      </div>
    );
  }

  // "ALL" shows every super category as its own section; a specific selection
  // is already scoped to one super category by the fetch above.
  const groups: [string, Category[]][] =
    selectedCategory === "ALL"
      ? Array.from(
          data.reduce((map, cat) => {
            const key = cat.superCategoryName || "Other";
            const list = map.get(key) ?? [];
            list.push(cat);
            map.set(key, list);
            return map;
          }, new Map<string, Category[]>()),
        )
      : [[selectedCategory, data]];

  return (
    <div className="nav-pill-scroll w-100">
      {groups.map(([superCategoryName, cats], idx) => (
        <div key={superCategoryName} className={idx > 0 ? "mt-4" : "mt-2"}>
          <b className="ms-1">{superCategoryName}</b>
          <div className="d-flex flex-wrap justify-content-start gap-3 align-items-start text-center mt-3 mb-3">
            {cats.map((cat) => (
              <Link
                key={cat._id}
                to={`/Categories/${encodeURIComponent(superCategoryName)}?category=${encodeURIComponent(cat.name)}`}
                className="text-decoration-none"
              >
                <div>
                  <div className="card mini-categories-cards border-radius-2xl">
                    <img
                      className="mini-category-images mt-0"
                      src={cat.image || FALLBACK_IMAGE}
                      alt={cat.name}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
                      }}
                    />
                  </div>
                  <div className="lh-1">
                    <span className="text-dark fw-normal text-xxs">{cat.name}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          {idx < groups.length - 1 && <hr className="horizontal dark" />}
        </div>
      ))}
    </div>
  );
};

export default MiniSubCategories;
